import { InMemoryCache, type DefaultContext } from '@apollo/client/core';
import type { NetworkError } from '@apollo/client/errors';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import ApolloService from 'ember-apollo-client/services/apollo';
import type { FormattedExecutionResult, GraphQLFormattedError } from 'graphql';
import type SessionService from './session';

const HTTP_STATUS_CODE = {
  FORBIDDEN: 403,
};

export default class ApiService extends ApolloService {
  @service('router') declare routerService: RouterService;
  @service('session') declare sessionService: SessionService;

  get authHeaders() {
    const { accessToken, guestToken } = this.sessionService;

    if (accessToken) {
      return {
        authorization: `Bearer ${accessToken}`,
      };
    }

    if (guestToken) {
      return {
        authorization: `Bearer ${guestToken}`,
      };
    }

    return {};
  }

  cache() {
    return new InMemoryCache({});
  }

  link() {
    const httpLink = super.link();

    const authLink = setContext(async (_, context) => {
      if (!this.sessionService.accessTokenValid) {
        await this.sessionService.fetchAccessToken();
      }

      this.addAuthorizationHeaders(context);

      return context;
    });

    const errorLink = onError(({ networkError, graphQLErrors, response }) => {
      this.handleGraphQLErrors(graphQLErrors, response);
      this.handleNetworkError(networkError);
    });

    return authLink.concat(errorLink).concat(httpLink);
  }

  addAuthorizationHeaders(context: DefaultContext) {
    context['headers'] = {
      ...context['headers'],
      ...this.authHeaders,
    } as DefaultContext;
  }

  handleNetworkError(networkError?: NetworkError) {
    if (
      networkError &&
      'statusCode' in networkError &&
      networkError?.statusCode === HTTP_STATUS_CODE.FORBIDDEN
    ) {
      this.routerService.transitionTo('forbidden');
    }
  }

  handleGraphQLErrors(
    graphQLErrors?: readonly GraphQLFormattedError[],
    response?: FormattedExecutionResult,
  ) {
    if (graphQLErrors) {
      if (graphQLErrors.every((error) => error.extensions?.['validation']) && response) {
        // strip out the first part of the key, e.g. projectInput.name becomes name
        const getKeyWithoutPrefix = (key: string) => {
          if (!key.includes('.')) {
            return key;
          }
          const parts = key.split('.');
          return parts.slice(1).join('.');
        };

        response.errors = graphQLErrors.map((error) => ({
          type: 'validation',
          message: error.message,
          validationErrors: Object.keys(error.extensions?.['validation'] || {}).reduce(
            (errors, key) => ({
              ...errors,
              [`${getKeyWithoutPrefix(key)}`]: (
                error.extensions?.['validation'] as Record<string, string>
              )?.[key],
            }),
            {},
          ),
        }));
        return;
      }

      if (graphQLErrors.some((error) => error.message === 'Unauthenticated.')) {
        console.error(`Unauthenticated`);
        this.sessionService.clearTokens();
        return;
      }

      if (graphQLErrors.some((error) => error.message === 'Internal server error')) {
        Array.from(graphQLErrors).forEach((error) => {
          console.error(`Internal server error`, error, error.extensions?.['debugMessage']);
        });
        return;
      }
      console.error('Uncaught Graphql Error: ', graphQLErrors);
    }
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:api')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('api') declare altName: ApiService;`.
declare module '@ember/service' {
  interface Registry {
    api: ApiService;
  }
}
